/**
 * Pixel position of a character index inside a <textarea>, via the well-known
 * mirror-div technique: clone the textarea's text into an absolutely-positioned
 * div with identical typography, place a marker at the index, and read its
 * offset. Used to anchor the autocomplete popover at the token the author is
 * typing. Defensive — any failure falls back to the textarea's bottom-left.
 */

const MIRRORED: (keyof CSSStyleDeclaration)[] = [
  'boxSizing', 'width', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
  'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
  'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'letterSpacing', 'lineHeight',
  'textTransform', 'wordSpacing', 'tabSize', 'whiteSpace', 'wordWrap',
];

export interface CaretCoords {
  top: number;
  left: number;
  height: number;
}

export function getCaretCoordinates(el: HTMLTextAreaElement, index: number): CaretCoords {
  try {
    const style = window.getComputedStyle(el);
    const div = document.createElement('div');
    for (const prop of MIRRORED) {
      // @ts-expect-error indexed style copy
      div.style[prop] = style[prop];
    }
    div.style.position = 'absolute';
    div.style.visibility = 'hidden';
    div.style.whiteSpace = 'pre-wrap';
    div.style.wordWrap = 'break-word';
    div.style.overflow = 'hidden';

    div.textContent = el.value.slice(0, index);
    const span = document.createElement('span');
    span.textContent = el.value.slice(index) || '.';
    div.appendChild(span);

    document.body.appendChild(div);
    const top = span.offsetTop + parseInt(style.borderTopWidth || '0', 10);
    const left = span.offsetLeft + parseInt(style.borderLeftWidth || '0', 10);
    const height = parseInt(style.lineHeight || '16', 10) || 16;
    document.body.removeChild(div);
    return { top, left, height };
  } catch {
    return { top: el.clientHeight, left: 0, height: 16 };
  }
}
